import React, { useEffect, useMemo, useState } from "react";
import {
  APPROVER_CARD_CONFIG,
  QmsVaultPersonCard,
  REVIEWER_CARD_CONFIG,
} from "./QmsVaultPersonCard";
import {
  buildVaultPeopleCycleTimeline,
  mapCycleMemberToPersonRow,
} from "./qmsVaultReviewCycleUtils";
import {
  canRemoveVaultApprover,
  canRemoveVaultReviewer,
} from "./qmsVaultWorkspaceUtils";

function formatCycleDate(value) {
  const text = String(value || "").trim();
  return text && text !== "—" ? text : "—";
}

function getCycleKey(cycle) {
  return `${cycle.startSort}-${cycle.endSort || "open"}`;
}

const MODE_CONFIG = {
  reviewers: {
    title: "Review cycles",
    ariaLabel: "Review cycle history",
    emptyMembers: "No reviewers found in this cycle.",
    cardConfig: REVIEWER_CARD_CONFIG,
    canRemove: canRemoveVaultReviewer,
    idField: "ReviewerId",
  },
  approvers: {
    title: "Approval cycles",
    ariaLabel: "Approval cycle history",
    emptyMembers: "No approvers found in this cycle.",
    cardConfig: APPROVER_CARD_CONFIG,
    canRemove: canRemoveVaultApprover,
    idField: "ApproverId",
  },
};

function ReviewCycleGroup({
  cycle,
  cycleKey,
  isCurrent,
  isExpanded,
  onToggle,
  showConnector,
  readOnly,
  canSave,
  isBusy,
  removingId,
  onRemoveRequest,
  modeConfig,
  mode,
}) {
  const isActiveCycle = cycle.status !== "completed";
  const members = cycle.members || cycle.reviewers || [];
  const itemClass = [
    "qms-vault-review-cycle__item",
    isExpanded ? "is-expanded" : "is-collapsed",
    isCurrent ? "is-current" : "is-past",
  ].join(" ");

  return (
    <article className={itemClass}>
      <div className="qms-vault-review-cycle__rail" aria-hidden="true">
        <span className="qms-vault-review-cycle__dot" />
        {showConnector ? <span className="qms-vault-review-cycle__line" /> : null}
      </div>

      <div className="qms-vault-review-cycle__group">
        <button
          type="button"
          className="qms-vault-review-cycle__cycle-toggle"
          aria-expanded={isExpanded}
          aria-controls={`people-cycle-panel-${cycleKey}`}
          onClick={onToggle}
        >
          <span className="qms-vault-review-cycle__cycle-dates">
            <span className="qms-vault-review-cycle__cycle-date">
              <span className="qms-vault-review-cycle__cycle-date-label">Start date</span>
              <span className="qms-vault-review-cycle__cycle-date-value">
                {formatCycleDate(cycle.startedAt)}
              </span>
            </span>
            <span className="qms-vault-review-cycle__cycle-date">
              <span className="qms-vault-review-cycle__cycle-date-label">End date</span>
              <span className="qms-vault-review-cycle__cycle-date-value">
                {formatCycleDate(cycle.completedAt)}
              </span>
            </span>
            {isCurrent ? (
              <span className="qms-vault-review-cycle__current-badge">Current</span>
            ) : cycle.closedByReject ? (
              <span className="qms-vault-review-cycle__past-badge is-rejected">Rejected</span>
            ) : (
              <span className="qms-vault-review-cycle__past-badge">Past</span>
            )}
          </span>
          <i
            className={`fas fa-chevron-${isExpanded ? "up" : "down"} qms-vault-review-cycle__cycle-chevron`}
            aria-hidden="true"
          />
        </button>

        {isExpanded ? (
          <div
            className="qms-vault-review-cycle__cards"
            id={`people-cycle-panel-${cycleKey}`}
          >
            {members.length ? (
              members.map((member) => {
                const row = mapCycleMemberToPersonRow(member, mode);
                const id = String(row[modeConfig.idField] || "");
                return (
                  <QmsVaultPersonCard
                    key={member.id}
                    row={row}
                    config={modeConfig.cardConfig}
                    readOnly={readOnly}
                    canSave={canSave && isActiveCycle}
                    canRemove={modeConfig.canRemove(row)}
                    isBusy={isBusy}
                    isRemoving={removingId === id}
                    onRemoveRequest={onRemoveRequest}
                  />
                );
              })
            ) : (
              <p className="qms-vault-review-cycle__empty-reviewers">
                {modeConfig.emptyMembers}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </article>
  );
}

function QmsVaultReviewCycleTimeline({
  mode = "reviewers",
  workspaceResponse,
  readOnly = false,
  canSave = false,
  isBusy = false,
  removingId = null,
  onRemoveRequest,
}) {
  const modeConfig = MODE_CONFIG[mode] || MODE_CONFIG.reviewers;
  const cycles = useMemo(
    () => buildVaultPeopleCycleTimeline(workspaceResponse, mode),
    [workspaceResponse, mode],
  );

  const currentCycle =
    cycles.find((cycle) => cycle.status !== "completed") || cycles[0] || null;
  const currentCycleKey = currentCycle ? getCycleKey(currentCycle) : null;
  const [expandedKeys, setExpandedKeys] = useState(() =>
    currentCycleKey ? new Set([currentCycleKey]) : new Set(),
  );

  // Reset only when current cycle changes. Do not depend on workspaceResponse —
  // refreshes were collapsing past cycles right after expand.
  useEffect(() => {
    if (!currentCycleKey) {
      setExpandedKeys(new Set());
      return;
    }
    setExpandedKeys(new Set([currentCycleKey]));
  }, [currentCycleKey]);

  const toggleCycle = (cycleKey) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(cycleKey)) {
        next.delete(cycleKey);
      } else {
        next.add(cycleKey);
      }
      return next;
    });
  };

  if (!cycles.length) {
    return null;
  }

  return (
    <section className="qms-vault-review-cycle" aria-label={modeConfig.ariaLabel}>
      <div className="qms-vault-review-cycle__title-wrap">
        <h6>{modeConfig.title}</h6>
        <span className="qms-vault-review-cycle__count">{cycles.length}</span>
      </div>

      <div className="qms-vault-review-cycle__list">
        {cycles.map((cycle, index) => {
          const cycleKey = getCycleKey(cycle);
          const isCurrent = cycle.status !== "completed";
          return (
            <ReviewCycleGroup
              key={cycleKey}
              cycle={cycle}
              cycleKey={cycleKey}
              isCurrent={isCurrent}
              isExpanded={expandedKeys.has(cycleKey)}
              onToggle={() => toggleCycle(cycleKey)}
              showConnector={index < cycles.length - 1}
              readOnly={readOnly}
              canSave={canSave}
              isBusy={isBusy}
              removingId={removingId}
              onRemoveRequest={onRemoveRequest}
              modeConfig={modeConfig}
              mode={mode}
            />
          );
        })}
      </div>
    </section>
  );
}

export default QmsVaultReviewCycleTimeline;
